import { createSlice, type PayloadAction } from "@reduxjs/toolkit"

interface PhoneNumber {
  id: string
  value: string
  isVerified: string;
  countryCode: string
  loading: boolean;
  error: string;
  duplicateError?: boolean;
}

interface PhoneNumbersState {
  phoneNumbers: PhoneNumber[]
  isOpenOtpModel: boolean
  verifiedNumbers: string[]
}

const initialState: PhoneNumbersState = {
  phoneNumbers: [{ id: "1", value: "", isVerified: "", countryCode: "", loading: false, error: '', duplicateError: false }],
  isOpenOtpModel: false,
  verifiedNumbers: []
}

const phoneNumbersSlice = createSlice({
  name: "phoneNumbers",
  initialState,
  reducers: {
    addPhoneNumber: (state) => {
      if (state.phoneNumbers.length < 5) {
        const newId = Date.now().toString()
        state.phoneNumbers.push({ id: newId, value: "", isVerified: "", countryCode: "", loading: false, error: '', duplicateError: false })
      }
    },
    removePhoneNumber: (state, action: PayloadAction<string>) => {
      if (state.phoneNumbers.length > 1) {
        state.phoneNumbers = state.phoneNumbers.filter((phone) => phone.id !== action.payload)
      }
    },
    updatePhoneNumber: (state, action: PayloadAction<{ id: string; field: string; value: string }>) => {
      const { id, field, value } = action.payload
      const phoneNumber = state.phoneNumbers.find((phone) => phone.id === id)
      if (phoneNumber) {
        phoneNumber.error = '';
        phoneNumber.duplicateError = false;
        if (field === "country_code") {
          phoneNumber.countryCode = value
        } else if (field === 'isVerified') {
          phoneNumber.isVerified = value
        } else {
          if (state.verifiedNumbers[Number(id) - 1] !== '') {
            if ('+' + value === state.verifiedNumbers[Number(id) - 1]) {
              phoneNumber.isVerified = "true"
            } else {
              phoneNumber.isVerified = "" 
            }
            phoneNumber.value = value
          }
        }
      }
    },
    setVerifiedSecondaryPhoneNumbers: (state, action) => {
      const { id, value } = action.payload;

      // Step 1: Create a dummy array of size 5 filled with existing values or empty strings
      let updatedArray = [...state.verifiedNumbers];

      // Step 2: Ensure the array is exactly size 5
      if (updatedArray.length !== 5) {
        updatedArray = Array(5).fill('').map((_, i) => updatedArray[i] || '');
      }

      // Step 3: Assign the new value at the specified index
      updatedArray[id] = value;

      // Step 4: Assign the updated array back to state
      state.verifiedNumbers = updatedArray;
    },
    setIsOpenOtpModel: (state, action: PayloadAction<boolean>) => {
      state.isOpenOtpModel = action.payload
    },
    setLoadingStateById: (state, action: PayloadAction<{ id: string; loading: boolean }>) => {
      const { id, loading } = action.payload;
      const phoneNumber = state.phoneNumbers.find((phone) => phone.id === id);
      if (phoneNumber) {
        phoneNumber.loading = loading;
      }
    },
    setIsVerifiedById: (state, action: PayloadAction<{ id: string; isVerified: string }>) => {
      const { id, isVerified } = action.payload;
      const phoneNumber = state.phoneNumbers.find((phone) => phone.id === id);
      if (phoneNumber) {
        phoneNumber.isVerified = isVerified; // Set isVerified to the provided boolean value
      }
    },
    setErrorById: (state, action: PayloadAction<{ id: string; error: string }>) => {
      const { id, error } = action.payload;
      const phoneNumber = state.phoneNumbers.find((phone) => phone.id === id);
      if (phoneNumber) {
        phoneNumber.error = error;
      }
    },
    setDuplicateErrorById: (state, action: PayloadAction<{ id: string; duplicateError: boolean }>) => {
      const { id, duplicateError } = action.payload;
      const phoneNumber = state.phoneNumbers.find((phone) => phone.id === id);
      if (phoneNumber) {
        phoneNumber.duplicateError = duplicateError;
      }
    },
    setPhoneNumber: (state, action) => {
      state.phoneNumbers = action.payload

    },
    resetPhoneNumbers: (state) => {
      state.phoneNumbers = initialState.phoneNumbers;
    }
  },
})

export const { addPhoneNumber, removePhoneNumber, setPhoneNumber, updatePhoneNumber, setIsOpenOtpModel, setLoadingStateById, setIsVerifiedById, setErrorById, setDuplicateErrorById, resetPhoneNumbers, setVerifiedSecondaryPhoneNumbers } = phoneNumbersSlice.actions
export default phoneNumbersSlice.reducer